import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Datetime from 'react-datetime';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import cn from 'classnames';
import { format } from 'date-fns';
import { get } from 'lodash';
import moment from 'moment';
import { toast } from 'react-toastify';

import LoansAgreementModal from 'components/LoansAgreementModal';
import CancelPaymentModal from 'components/CancelPaymentModal';
import Validator from 'components/Validator/Validator';
import Loader from 'components/Loader';
import { ApplePayButton, Button, ButtonLink, SuggestApplePayButton } from 'components/Button';
import Input from 'components/Form/Input';
import { getCurrentDate } from 'utils/func';
import { formatCurrency } from 'utils/formatCurrency';
import { checkIfApplePayIsAvailable } from 'utils/helper';
import { unmask } from 'utils/masks';
import {
  getQuickpayCollateralRequiredAction,
  getQuickpayCollateralSummaryAction,
  getQuickpayLoanSetupAction,
  makeQuickpayPaymentAction,
} from 'actions/quickpay';
import {
  buildCollateralGoalOptionsFromDisplay,
  getCollateralRequiredDisplay,
  getCollateralSummaryHeldDisplay,
  resolveFirstDefined,
  toLtvPercentage,
} from 'containers/pages/Dashboard/utils/cryptoUtils';
import LtvSummaryCard from 'containers/pages/Dashboard/components/LowerLtvModal/LtvSummaryCard';
import PaymentActionToggle, { LOWER_LTV_ACTIONS } from 'containers/pages/Dashboard/components/LowerLtvModal/PaymentActionToggle';
import ChooseYourGoal from 'containers/pages/Dashboard/components/LowerLtvModal/ChooseYourGoal';
import GoalAmountSummary from 'containers/pages/Dashboard/components/LowerLtvModal/GoalAmountSummary';
import LowerLtvPaymentForm from 'containers/pages/Dashboard/components/LowerLtvModal/LowerLtvPaymentForm';
import QuickPayCollateralDeposit from './QuickPayCollateralDeposit';
import { getQuickpayLoanSetupThresholds } from './cryptoUtils';
import 'containers/pages/Dashboard/components/LowerLtvModal/style.scss';

import styles from './styles.module.scss';
import paymentSchema from './schemas/makePaymentSchema';
import applePaySchema from './schemas/applePaySchema';

const DEFAULT_MARGIN_CALL_THRESHOLD = 75;
const DEFAULT_LIQUIDATION_THRESHOLD = 90;

const CRYPTO_GOAL_TYPES = {
  MINIMUM: 'minimum',
  SUGGESTED: 'suggested',
};

const PAYMENT_AMOUNT_SUGGESTED = 'goal';

const LoverYourLtv = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const quickpayData = useSelector((state) => state.quickpay.data);
  const collateralSummaryState = useSelector((state) => state.quickpay.collateralSummary);
  const collateralRequiredState = useSelector((state) => state.quickpay.collateralRequired);
  const loanSetupState = useSelector((state) => state.quickpay.loanSetup);
  const paymentConfirmedAmount = location.state?.paymentConfirmedAmount;
  const [paymentModal, setPaymentModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [paymentAmountType, setPaymentAmountType] = useState('0');
  const [customPaymentAmount, setCustomPaymentAmount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [cryptoAction, setCryptoAction] = useState(LOWER_LTV_ACTIONS.COLLATERAL);
  const [walletCopied, setWalletCopied] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(CRYPTO_GOAL_TYPES.MINIMUM);

  const { loanId } = quickpayData;
  const collateralSummary = collateralSummaryState?.data;
  const quickpayCollateralRequired = collateralRequiredState?.data;
  const isSummaryLoading = collateralSummaryState?.isLoading;
  const isCollateralRequiredLoading = collateralRequiredState?.isLoading;
  const isApplePayAvailable = checkIfApplePayIsAvailable();

  const {
    validator: {
      values,
      setValues,
      onChangeHandler,
      validate,
      errors,
    },
  } = props;

  useEffect(() => {
    const primaryPaymentMethod = (quickpayData.paymentAccounts || []).find((acc) => acc.isPrimary);
    setValues({
      paymentAccountId: primaryPaymentMethod?.accountId,
      applyDate: getCurrentDate(),
      amount: quickpayData.totalDue,
      selectedAmountOption: '0',
      rawData: quickpayData,
    });
    setPaymentAmountType('0');
  }, []);

  useEffect(() => {
    if (!loanId) {
      return;
    }

    dispatch(getQuickpayLoanSetupAction({ loanId }));
    dispatch(getQuickpayCollateralSummaryAction({
      loanId,
    }));
    dispatch(getQuickpayCollateralRequiredAction({
      loanId,
    }));
  }, [dispatch, loanId]);

  const collateralHeldDisplay = useMemo(
    () => getCollateralSummaryHeldDisplay(collateralSummary).collateralHeld || '-',
    [collateralSummary]
  );

  const collateralRequiredData = useMemo(
    () => getCollateralRequiredDisplay(quickpayCollateralRequired),
    [quickpayCollateralRequired]
  );

  const currentLtv = collateralSummary?.currentLtv;
  const {
    marginCallPct: apiMarginCallPct,
    liquidationPct: apiLiquidationPct,
    contractualLtvPct: apiContractualLtvPct,
  } = getQuickpayLoanSetupThresholds(loanSetupState?.data);
  const marginCallThreshold = apiMarginCallPct != null
    ? Math.round(apiMarginCallPct)
    : DEFAULT_MARGIN_CALL_THRESHOLD;
  const liquidationThreshold = apiLiquidationPct != null
    ? Math.round(apiLiquidationPct)
    : DEFAULT_LIQUIDATION_THRESHOLD;

  const contractualLtvPct = apiContractualLtvPct != null ? Math.round(apiContractualLtvPct) : 70;
  const coinType = (collateralRequiredData.coin || collateralSummary?.coin || 'BTC').toUpperCase();
  const currentLtvPct = toLtvPercentage(currentLtv);
  const showGoalPicker = marginCallThreshold != null
    && currentLtvPct != null
    && currentLtvPct > marginCallThreshold;
  // Below margin call: allow editing payment amount + date. At/above: account only.
  const allowAmountAndDateEdit = !(
    currentLtvPct != null
    && marginCallThreshold != null
    && currentLtvPct >= marginCallThreshold
  );

  const goalOptions = useMemo(
    () => {
      if (!showGoalPicker) return [];

      const collateralOptions = buildCollateralGoalOptionsFromDisplay(collateralRequiredData, coinType, {
        ensureBothCards: true,
        targetLtvTrackLabel: contractualLtvPct,
      });

      if (cryptoAction === LOWER_LTV_ACTIONS.PAYMENT) {
        return collateralOptions.map((option) => {
          if (option.key === 'minimum') {
            const payAmt = collateralRequiredData.minimumPaymentRequired;
            return {
              ...option,
              amount: payAmt != null ? String(payAmt) : '',
              displayAmount: payAmt != null ? `$${formatCurrency(payAmt, 2)}` : '-',
            };
          }
          const payAmt = resolveFirstDefined(
            collateralRequiredData.paymentRequired,
            collateralRequiredData.suggestedPaymentRequired
          );
          return {
            ...option,
            amount: payAmt != null ? String(payAmt) : '',
            displayAmount: payAmt != null ? `$${formatCurrency(payAmt, 2)}` : '-',
          };
        });
      }

      return collateralOptions;
    },
    [collateralRequiredData, coinType, showGoalPicker, cryptoAction]
  );

  const activeGoal = useMemo(
    () => goalOptions.find((option) => option.key === selectedGoal) || goalOptions[0],
    [goalOptions, selectedGoal]
  );

  useEffect(() => {
    if (!goalOptions.length) {
      return;
    }

    if (!goalOptions.some((option) => option.key === selectedGoal)) {
      setSelectedGoal(goalOptions[0].key);
    }
  }, [goalOptions, selectedGoal]);

  const depositAmount = showGoalPicker
    ? (resolveFirstDefined(
      activeGoal?.amount,
      selectedGoal === CRYPTO_GOAL_TYPES.MINIMUM
        ? collateralRequiredData.minimumQuantityRequired
        : collateralRequiredData.suggestedQuantityRequired,
      collateralRequiredData.quantityRequired
    ) || '0')
    : (resolveFirstDefined(
      collateralRequiredData.quantityRequired,
      collateralRequiredData.suggestedQuantityRequired
    ) || '0');
  const goalLinkedPaymentAmount = showGoalPicker
    ? resolveFirstDefined(
      activeGoal?.paymentAmount,
      selectedGoal === CRYPTO_GOAL_TYPES.MINIMUM
        ? collateralRequiredData.minimumPaymentRequired
        : resolveFirstDefined(
          collateralRequiredData.paymentRequired,
          collateralRequiredData.suggestedPaymentRequired
        ),
      collateralRequiredData.paymentRequired,
      collateralRequiredData.minimumPaymentRequired,
      collateralRequiredData.suggestedPaymentRequired
    )
    : resolveFirstDefined(
      collateralRequiredData.paymentRequired,
      collateralRequiredData.suggestedPaymentRequired,
      collateralRequiredData.minimumPaymentRequired
    );
  const walletAddress = String(collateralRequiredData.address ?? '').trim();
  const hasWalletAddress = Boolean(walletAddress);

  const copyWalletToClipboard = async () => {
    if (!hasWalletAddress) {
      return;
    }

    try {
      await navigator.clipboard.writeText(walletAddress);
      setWalletCopied(true);
    } catch {
      toast.error('Unable to copy address. Please copy it manually.');
    }
  };

  const goalSummaryLabel = useMemo(() => {
    if (cryptoAction === LOWER_LTV_ACTIONS.COLLATERAL) {
      return 'TRANSFER AMOUNT';
    }

    return `${(activeGoal?.label || 'Suggested').toUpperCase()} EXTRA PAYMENT`;
  }, [cryptoAction, activeGoal?.label]);

  const goalSummaryDescription = useMemo(() => {
    if (cryptoAction === LOWER_LTV_ACTIONS.PAYMENT) {
      if (showGoalPicker && selectedGoal === CRYPTO_GOAL_TYPES.MINIMUM) {
        return 'Minimum to stop your margin call';
      }

      return `Suggested: returns to target ${contractualLtvPct}% LTV`;
    }

    return activeGoal?.description || `Returns to your contract LTV of ${contractualLtvPct}%.`;
  }, [cryptoAction, showGoalPicker, selectedGoal, activeGoal?.description, contractualLtvPct]);

  const allPaymentMethods = (quickpayData?.paymentAccounts || []).map((acc) => ({
    value: acc.accountId,
    title: `${(acc.accountType)} ${acc.category === 'bankcard' ? `xxxx-xxxx-xxxx-${acc.cardNumberLast4}` : `xxxx${acc.accountNumberLast4}`}`,
  }));

  allPaymentMethods.push({
    value: 'addPaymentMethod',
    title: '+ Add Payment Method ...',
  });

  const paymentAmounts = useMemo(() => {
    const baseOptions = [
      {
        title: `Total Due - $${quickpayData.totalDue}`,
        value: '0',
      },
      {
        title: `Past Due (including fees) - $${formatCurrency(quickpayData.amountPastDue + quickpayData.dueFees, 2)}`,
        value: '1',
      },
      {
        title: 'Other Amount',
        value: '2',
      },
    ];

    if (cryptoAction !== LOWER_LTV_ACTIONS.PAYMENT || goalLinkedPaymentAmount == null) {
      return baseOptions;
    }

    return [
      {
        title: `${activeGoal?.label || 'Suggested'} - $${formatCurrency(goalLinkedPaymentAmount, 2)}`,
        value: PAYMENT_AMOUNT_SUGGESTED,
      },
      ...baseOptions,
    ];
  }, [cryptoAction, goalLinkedPaymentAmount, quickpayData, activeGoal?.label]);

  const payFromAccountLabel = useMemo(() => {
    const account = allPaymentMethods.find((item) => item.value === values.paymentAccountId);
    return account?.title || '';
  }, [allPaymentMethods, values.paymentAccountId]);

  const paymentAmountLabel = useMemo(() => {
    if (paymentAmountType === PAYMENT_AMOUNT_SUGGESTED && goalLinkedPaymentAmount != null) {
      const goalTag = (activeGoal?.label || 'suggested').toLowerCase();
      return `$${formatCurrency(goalLinkedPaymentAmount, 2)} (${goalTag})`;
    }
    const option = paymentAmounts.find((item) => item.value === paymentAmountType);
    if (paymentAmountType === '2' && customPaymentAmount) {
      return customPaymentAmount;
    }
    return option?.title || `$${formatCurrency(values.amount, 2)}`;
  }, [
    paymentAmounts,
    paymentAmountType,
    customPaymentAmount,
    values.amount,
    goalLinkedPaymentAmount,
    activeGoal?.label,
  ]);

  const isSuggestedPaymentAmount = paymentAmountType === PAYMENT_AMOUNT_SUGGESTED
    && goalLinkedPaymentAmount != null;

  useEffect(() => {
    if (cryptoAction !== LOWER_LTV_ACTIONS.PAYMENT || goalLinkedPaymentAmount == null) {
      return;
    }

    setPaymentAmountType(PAYMENT_AMOUNT_SUGGESTED);
    setCustomPaymentAmount(formatCurrency(goalLinkedPaymentAmount, 2));
  }, [cryptoAction, goalLinkedPaymentAmount, selectedGoal]);

  const paymentDate = useMemo(() => {
    if (quickpayData.nextPaymentDate) {
      return moment(quickpayData.nextPaymentDate).format('MM/DD/YYYY');
    }
    return '';
  }, [quickpayData.nextPaymentDate]);

  const toggleModal = (type) => {
    if (type === 'cancel') {
      setCancelModal(!cancelModal);
      setPaymentModal(false);
    } else {
      setPaymentModal(!paymentModal);
      setCancelModal(false);
    }
  };

  const getValidationValues = () => ({
    ...values,
    selectedAmountOption: paymentAmountType,
    customPaymentAmount,
  });

  const getAmountToPay = (isApplePay = false) => {
    let nextAmount = 0;
    switch (paymentAmountType) {
      case '0':
        nextAmount = isApplePay ? quickpayData.totalDue : (quickpayData.totalDue - quickpayData.dueFees);
        break;
      case '1':
        nextAmount = quickpayData.amountPastDue + quickpayData.dueFees;
        break;
      case '2':
        nextAmount = Number(unmask(customPaymentAmount));
        break;
      case PAYMENT_AMOUNT_SUGGESTED:
        nextAmount = Number(goalLinkedPaymentAmount) || 0;
        break;
      default:
        break;
    }
    return nextAmount;
  };

  const handlePayFromAccountChange = (event) => {
    if (event.target.value === 'addPaymentMethod') {
      navigate('/quickpay/add-payment-method', {
        state: { afterAddPaymentMethod: '/quickpay/lower-your-ltv' },
      });
    } else {
      onChangeHandler('paymentAccountId', event.target.value);
    }
  };

  const handlePaymentAmountTypeChange = (event) => {
    setPaymentAmountType(event.target.value);
  };

  const handleCustomPaymentAmountChange = (event) => {
    setCustomPaymentAmount(event.target.value);
  };

  const handleDateChange = (date) => {
    if (date && typeof date.toISOString === 'function') {
      onChangeHandler('applyDate', format(new Date(date.toISOString()), 'MM/dd/yyyy'));
    } else {
      onChangeHandler('applyDate', date);
    }
  };

  const validateDate = (current) => {
    if (get(quickpayData, 'daysPastDue', 0) > 45) {
      return current.isAfter(moment().subtract(1, 'day')) && current.isBefore(moment());
    }
    return current.isAfter(moment().subtract(1, 'day')) && current.isBefore(moment().add(45 - get(quickpayData, 'daysPastDue', 0), 'days'));
  };

  const validatePaymentRequest = (schema) => validate(schema, getValidationValues()).isValid;

  const handlePostPayment = (applePayData, successCallback, failCallback) => {
    setErrorMsg('');
    const schema = applePayData ? applePaySchema : paymentSchema;

    if (validatePaymentRequest(schema)) {
      setIsLoading(true);
      const selectedAccount = quickpayData.paymentAccounts.find((acc) => acc.accountId === values.paymentAccountId);
      const paymentMethodCode = applePayData ? 'apple-pay' : selectedAccount.category === 'bankcard' ? 'debit-card' : 'bank-account';
      let amountToPay = getAmountToPay();
      let fees = quickpayData.dueFees;

      if (paymentAmountType === '1') {
        amountToPay = quickpayData.amountPastDue;
      } else if (paymentAmountType === '2' || paymentAmountType === PAYMENT_AMOUNT_SUGGESTED) {
        fees = 0;
      }

      dispatch(makeQuickpayPaymentAction({
        data: {
          paymentAccountId: !applePayData ? values.paymentAccountId : undefined,
          paymentMethodCode,
          applyDate: moment(values.applyDate).format('YYYY-MM-DD'),
          amount: amountToPay,
          fees,
          isPayoff: false,
          applePay: applePayData || undefined,
        },
        success: (res) => {
          setIsLoading(false);
          if (res && res.some((item) => get(item, 'paymentProcessor.status') === 'Failed')) {
            setErrorMsg('We encountered an error while processing payment.');
            failCallback && failCallback();
          } else {
            navigate('/quickpay/payment-submitted');
            successCallback && successCallback();
          }
        },
        fail: (err) => {
          setErrorMsg(get(err, 'data.message') || get(err, 'data.errorMessages.0') || 'An error occured.');
          setIsLoading(false);
          failCallback && failCallback();
        },
      }));
    }
  };

  const primaryPaymentMethod = (quickpayData.paymentAccounts || []).find((acc) => acc.isPrimary);

  const renderPaymentDateField = ({ isDisabled } = {}) => (
    <div
      className="modal-dialog"
      style={{
        margin: 0,
        maxWidth: 'none',
        width: '100%',
        pointerEvents: 'auto',
      }}
    >
      <div className={cn('has-value', 'hasValue', 'w-100', 'mb-2', errors?.applyDate ? 'required' : '')}>
        <span>
          Payment Date
          <em>Required</em>
        </span>
        <Datetime
          name="applyDate"
          label="Payment Date"
          value={values.applyDate}
          onChange={isDisabled ? undefined : handleDateChange}
          closeOnSelect
          dateFormat="MM/DD/YYYY"
          timeFormat={false}
          placeHolder="MM/DD/YYYY"
          isValidDate={validateDate}
          inputProps={{ disabled: isDisabled }}
          renderInput={(propsInput) => (
            <Input
              isMasked={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
              isRequired
              hasError={!!errors.applyDate}
              errorMessage={errors.applyDate}
              isDisabled={isDisabled}
              {...propsInput}
            />
          )}
        />
      </div>
    </div>
  );

  const renderCryptoSection = () => {
    if (isSummaryLoading) {
      return <Loader className="height-2 mb-2" />;
    }

    return (
      <>
        <LtvSummaryCard
          collateralHeld={collateralHeldDisplay}
          currentLtv={currentLtv}
          contractualLtvPct={contractualLtvPct}
          marginCallThreshold={marginCallThreshold}
          liquidationThreshold={liquidationThreshold}
        />

        <PaymentActionToggle
          activeAction={cryptoAction}
          onChange={setCryptoAction}
        />

        {showGoalPicker && (
          <ChooseYourGoal
            options={goalOptions}
            selectedGoal={selectedGoal}
            onSelect={setSelectedGoal}
            isLoading={isCollateralRequiredLoading}
          />
        )}

        {cryptoAction === LOWER_LTV_ACTIONS.PAYMENT && !showGoalPicker && (
          <GoalAmountSummary
            variant="payment"
            amount={goalLinkedPaymentAmount}
            coinType={coinType}
            contractualLtvDisplay={`${contractualLtvPct}%`}
            description={goalSummaryDescription}
            summaryLabel={goalSummaryLabel}
          />
        )}

        {cryptoAction === LOWER_LTV_ACTIONS.COLLATERAL && !showGoalPicker && (
          <GoalAmountSummary
            variant="deposit"
            amount={depositAmount}
            coinType={coinType}
            contractualLtvDisplay={`${contractualLtvPct}%`}
            description={goalSummaryDescription}
            summaryLabel={goalSummaryLabel}
          />
        )}

        {cryptoAction === LOWER_LTV_ACTIONS.PAYMENT ? (
          <LowerLtvPaymentForm
            payFromAccounts={allPaymentMethods}
            payFromAccount={values.paymentAccountId}
            paymentAmountOptions={paymentAmounts}
            paymentAmountType={paymentAmountType}
            customPaymentAmount={customPaymentAmount}
            paymentDate={values.applyDate}
            payFromAccountLabel={payFromAccountLabel}
            paymentAmountLabel={paymentAmountLabel}
            isGoalLinkedPaymentAmount={isSuggestedPaymentAmount}
            onPayFromAccountChange={handlePayFromAccountChange}
            onPaymentAmountTypeChange={handlePaymentAmountTypeChange}
            onCustomPaymentAmountChange={handleCustomPaymentAmountChange}
            renderPaymentDate={renderPaymentDateField}
            allowAmountAndDateEdit={allowAmountAndDateEdit}
            initialEditing={!primaryPaymentMethod}
            pastDueAmount={paymentConfirmedAmount ? '' : ((Number(quickpayData.amountPastDue) || 0) > 0 ? formatCurrency(quickpayData.amountPastDue, 2) : '')}
            regularPaymentAmount={formatCurrency(quickpayData.totalDue, 2)}
            nextPaymentDueDate={paymentDate}
          />
        ) : (
          <QuickPayCollateralDeposit
            depositAmount={depositAmount}
            coinType={coinType}
            regularPaymentAmount={paymentConfirmedAmount ? '' : formatCurrency(quickpayData.totalDue, 2)}
            nextPaymentDueDate={paymentConfirmedAmount ? '' : paymentDate}
            pastDueAmount={paymentConfirmedAmount ? '' : ((Number(quickpayData.amountPastDue) || 0) > 0 ? formatCurrency(quickpayData.amountPastDue, 2) : '')}
            walletAddress={walletAddress}
            showAmountBox={false}
          />
        )}
      </>
    );
  };

  const showPaymentFooter = cryptoAction === LOWER_LTV_ACTIONS.PAYMENT;
  const showCollateralFooter = cryptoAction === LOWER_LTV_ACTIONS.COLLATERAL && !isSummaryLoading;

  return (
    <div className={cn(styles.quickpay, styles.simplepay)}>
      <div className={cn(styles.content, 'text-center')}>
        {paymentConfirmedAmount ? (
          <div className={styles.payPastDueConfirmedBanner} role="status">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="10" fill="none" stroke="#16a34a" strokeWidth="2" />
              <path
                d="M8 12.5 10.5 15 16 9.5"
                fill="none"
                stroke="#16a34a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>
              {`Payment of $${formatCurrency(paymentConfirmedAmount, 2)} confirmed and being processed`}
            </span>
          </div>
        ) : null}
        <h2>
          Hey
          {' '}
          {quickpayData.firstName}
          !
        </h2>
        {paymentConfirmedAmount ? (
          <p className={styles.ltvDriftedWarning} style={{ color: '#e67e22', fontWeight: 600 }}>Now protect your collateral</p>
        ) : null}
        <div className="lower-ltv-modal lower-ltv-modal--embedded">
          {renderCryptoSection()}
        </div>
      </div>
      <div className={styles.stickyFooter}>
        {errorMsg ? (
          <div className={cn(styles.error, 'mb-1')}>
            {errorMsg}
          </div>
        ) : null}

        {showPaymentFooter ? (
          <>
            <p className="lower-ltv-modal__authorization" style={{ fontSize: '13px' }}>
              By clicking
              {isApplePayAvailable ? ' "Apple Pay" or ' : ' '}
              &quot;Make Extra Payment&quot;, I authorize LendingUSA to make a one-time
              debit and/or charge from my account as designated above.
            </p>
            <div className="mt-1 mb-2 w-100">
              <Button
                color="primary"
                className="lower-ltv-modal__submit-btn w-100"
                onClick={() => handlePostPayment()}
                isLoading={isLoading}
              >
                Make Extra Payment
              </Button>
              {isApplePayAvailable ? (
                <ApplePayButton
                  className="w-100"
                  getAmountToPay={() => getAmountToPay(true)}
                  processPayment={handlePostPayment}
                  validatePayment={() => validatePaymentRequest(applePaySchema)}
                />
              ) : (
                <SuggestApplePayButton loanId={loanId} />
              )}
            </div>
          </>
        ) : null}

        {showCollateralFooter ? (
          <div className="mt-1 mb-2 w-100">
            <Button
              color="primary"
              className="lower-ltv-modal__submit-btn w-100"
              onClick={copyWalletToClipboard}
              isDisabled={!hasWalletAddress}
            >
              {walletCopied ? 'Copied!' : 'Copy Wallet Address'}
            </Button>
          </div>
        ) : null}

        {showPaymentFooter && (
          <>
            <div style={{ textAlign: 'center', marginTop: 5 }}>
              <ButtonLink
                className="d-block text-center btn-link"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleModal('payment')}
              >
                How will my payment be applied?
              </ButtonLink>
            </div>
            <div style={{ textAlign: 'center', marginTop: 5 }}>
              <ButtonLink
                className="d-block text-center btn-link"
                style={{ cursor: 'pointer' }}
                onClick={() => toggleModal('cancel')}
              >
                How can I cancel my payment?
              </ButtonLink>
            </div>
          </>
        )}
      </div>
      <LoansAgreementModal
        className={styles.modal}
        isOpen={paymentModal}
        toggle={() => toggleModal('payment')}
        paddingTop={1}
        showCloseBtn
        footerLink={<div style={{ marginTop: 16 }} />}
      />
      <CancelPaymentModal
        className={styles.modal}
        isOpen={cancelModal}
        toggle={() => toggleModal('cancel')}
        paddingTop={1}
        showCloseBtn
        footerLink={<div style={{ marginTop: 16 }} />}
      />
    </div>
  );
};

LoverYourLtv.propTypes = {
  validator: PropTypes.object.isRequired,
};

export default compose(Validator(paymentSchema))(LoverYourLtv);
